import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import type { GuidedEditPayload } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreateCampaignModal";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import CreativeBlockEditor from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreativeBlockEditor";
import type { CreativeBlock } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/guidedFlow/types";
import {
  hydratePartnerFieldsFromGuidedWire,
  mapCreativeBlockToGuidedCreative,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/mapCreativeBlockToGuidedCreative";
import {
  instagramAccountsForSelectOptions,
  instagramSelectOptionsFromAccounts,
  resolveAdvertiserInstagramForCreativeBlock,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/creativesWithInstagramFromFetch";
import { mergeInstagramAccounts } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/mergeInstagramAccounts";
import {
  hasMixedGuidedCreativeMediaTypes,
  MATCHING_CREATIVE_MEDIA_TYPES_ERROR,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/validateCreativeMediaType";
import { creativeBlockHasPartnerPost } from "@common/utils/instagramPartnershipCreative";
import { creativeDestinationLinkError } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/creativeDestinationLink";
import { isManagedAdsPartnerPostsEnabledForOrg } from "src/managedAdsPartnerPostsAccess";
import {
  partnershipCatalogEditorSliceForKey,
  partnershipIdentityCatalogToEditorState,
  type PartnershipPartnerOptionsLoad,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/fetchPartnershipPartnerOptions";
import { mergePartnerFacebookPagesForPicker } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/mapPartnershipPartnerOptionsResponse";
import {
  buildPartnershipPartnerOptionsFetchParamsFromCreative,
  partnershipPartnerOptionsFetchKey,
  resolveAdvertiserIdentityForPartnership,
  useDebouncedPartnershipPartnerOptionsLoads,
  type RefetchPartnershipPartnerOptions,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/partnershipPartnerOptionsFetch";
import {
  partnerInstagramFieldsFromLinkedFacebookPage,
  resolveCreativePartnerFacebookPage,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/resolveCreativePartnerDisplay";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type GuidedCreative = GuidedEditPayload["creatives"][number];

type EditAdCreativeModalProps = {
  isOpen: boolean;
  isSaving?: boolean;
  /** When true, Facebook pages for the ad account are still loading. */
  isFacebookPagesLoading?: boolean;
  /** When true, Instagram accounts for the selected page are still loading. */
  isInstagramAccountsLoading?: boolean;
  /** When true, another creative in the same ad set has Instagram set; this modal cannot clear Instagram. */
  otherAdsInAdsetHaveInstagram?: boolean;
  creative: GuidedCreative;
  pages: FacebookPage[];
  partnerPages?: FacebookPage[];
  partnerInstagramAccounts?: InstagramAccount[];
  instagramAccounts: InstagramAccount[];
  createUploadUrl: (
    file: File,
    callback: (params: {
      signedUrl?: string;
      fileKey?: string;
      file_key?: string;
      cdn_url?: string;
    }) => void
  ) => void;
  onClose: () => void;
  onSave: (creative: GuidedCreative) => void | Promise<void>;
  onFacebookPageChange?: (
    facebookPageId: string
  ) => void | Promise<InstagramAccount[] | void>;
  onPartnerFacebookPageChange?: (
    facebookPageId: string
  ) => void | Promise<InstagramAccount[] | void>;
  adAccountId?: string;
  partnershipOptionsLoading?: boolean;
  partnershipPermissionsLoaded?: boolean;
  partnershipOptionsError?: string | null;
  metaBusinessId?: string;
  advertiserValidationMessages?: string[];
  onRefetchPartnershipPartnerOptions?: RefetchPartnershipPartnerOptions;
  onFetchLatestMetaIdentity?: () => void | Promise<void>;
  partnershipCatalogByIdentityKey?: Readonly<
    Record<string, PartnershipPartnerOptionsLoad>
  >;
  organizationId?: string;
};

export default function EditAdCreativeModal({
  isOpen,
  isSaving = false,
  isFacebookPagesLoading = false,
  isInstagramAccountsLoading = false,
  otherAdsInAdsetHaveInstagram = false,
  creative,
  pages,
  partnerPages = [],
  partnerInstagramAccounts = [],
  instagramAccounts,
  createUploadUrl,
  onClose,
  onSave,
  adAccountId,
  partnershipOptionsLoading = false,
  partnershipPermissionsLoaded = false,
  partnershipOptionsError = null,
  metaBusinessId,
  advertiserValidationMessages = [],
  onRefetchPartnershipPartnerOptions,
  onFetchLatestMetaIdentity,
  partnershipCatalogByIdentityKey = {},
  organizationId,
}: EditAdCreativeModalProps) {
  const partnerPostsEnabled =
    isManagedAdsPartnerPostsEnabledForOrg(organizationId);
  const [
    partnershipPermissionLookupLoading,
    setPartnershipPermissionLookupLoading,
  ] = useState(false);
  const [isManualMetaIdentityRefreshing, setIsManualMetaIdentityRefreshing] =
    useState(false);
  const [value, setValue] = useState<CreativeBlock>({
    primaryText: "",
    fbPage: null,
    instagramAccount: null,
  });
  const [editingCreativeName, setEditingCreativeName] = useState<{
    index: number;
    value: string;
  } | null>(null);
  const uploadRefs = useRef<
    Record<string, { fileKey?: string; cdn_url?: string }>
  >({});
  const lastModalSourceKeyRef = useRef("");

  const advertiserInstagramCatalog = useMemo(
    () =>
      instagramAccountsForSelectOptions(
        instagramAccounts,
        value.instagramAccount ?? undefined
      ),
    [instagramAccounts, value.instagramAccount]
  );

  const partnerPostActive =
    partnerPostsEnabled && creativeBlockHasPartnerPost(value);

  useEffect(() => {
    if (!isOpen) {
      setPartnershipPermissionLookupLoading(false);
    }
  }, [isOpen]);

  const mergedPartnerPagesByIdentityKey = useMemo(() => {
    const map = new Map<string, FacebookPage[]>();
    map.set("", mergePartnerFacebookPagesForPicker(pages, partnerPages));
    for (const [key, catalog] of Object.entries(partnershipCatalogByIdentityKey)) {
      map.set(
        key,
        mergePartnerFacebookPagesForPicker(
          pages,
          partnershipIdentityCatalogToEditorState(catalog).partnerPages
        )
      );
    }
    return map;
  }, [pages, partnerPages, partnershipCatalogByIdentityKey]);

  const partnershipFetchParams = useMemo(
    () =>
      adAccountId?.trim()
        ? buildPartnershipPartnerOptionsFetchParamsFromCreative({
            adAccountId,
            creative: value,
            advertiserInstagramCatalog,
          })
        : null,
    [adAccountId, advertiserInstagramCatalog, value]
  );

  const partnershipFetchKey = partnershipFetchParams
    ? partnershipPartnerOptionsFetchKey(partnershipFetchParams)
    : "";

  const partnershipCatalogSlice = useMemo(
    () =>
      partnershipFetchKey
        ? partnershipCatalogEditorSliceForKey(
            partnershipCatalogByIdentityKey,
            partnershipFetchKey
          )
        : undefined,
    [partnershipCatalogByIdentityKey, partnershipFetchKey]
  );

  const resolvedPartnerPages = useMemo(
    () =>
      mergedPartnerPagesByIdentityKey.get(partnershipFetchKey) ??
      mergedPartnerPagesByIdentityKey.get("") ??
      partnerPages,
    [mergedPartnerPagesByIdentityKey, partnerPages, partnershipFetchKey]
  );

  const resolvedPartnerInstagramAccounts = useMemo(
    () =>
      mergeInstagramAccounts([
        ...(partnershipCatalogSlice?.partnerInstagramAccounts ??
          partnerInstagramAccounts),
      ]),
    [partnerInstagramAccounts, partnershipCatalogSlice?.partnerInstagramAccounts]
  );

  const modalSourceKey = useMemo(
    () =>
      [
        creative.fb_page_id,
        creative.instagram_id,
        creative.primary_text ?? "",
        creative.link ?? "",
        creative.square_url ?? "",
        creative.square_file_key ?? "",
        creative.portrait_url ?? "",
        creative.portrait_file_key ?? "",
        creative.partner_post_enabled ?? "",
        creative.partner_instagram_user_id ?? "",
        creative.partner_instagram_username ?? "",
        creative.partner_facebook_page_id ?? "",
        creative.partner_permission_status ?? "",
        pages
          .map((p) => p.id)
          .sort()
          .join(","),
      ].join("|"),
    [creative, pages]
  );

  /**
   * When the server creative or page list identity changes, replace the editor from
   * props. When only Instagram rows load (same source key), merge account objects
   * without overwriting a Facebook page the user already picked in the modal.
   */
  useEffect(() => {
    if (!isOpen) {
      lastModalSourceKeyRef.current = "";
      return;
    }

    const replaceFromServer = lastModalSourceKeyRef.current !== modalSourceKey;
    if (replaceFromServer) {
      lastModalSourceKeyRef.current = modalSourceKey;
    }

    setValue((prev) => {
      if (replaceFromServer) {
        const mappedPage =
          pages.find((page) => page.id === creative.fb_page_id) ?? null;
        return {
          name: creative.name,
          primaryText: creative.primary_text ?? "",
          link: creative.link ?? "",
          squareMediaUrl: creative.square_url,
          squareMediaFileKey: creative.square_file_key,
          squareMediaType: creative.square_media_type,
          verticalMediaUrl: creative.portrait_url,
          verticalMediaFileKey: creative.portrait_file_key,
          verticalMediaType: creative.portrait_media_type,
          fbPage: mappedPage,
          instagramAccount: resolveAdvertiserInstagramForCreativeBlock({
            fbPage: mappedPage,
            instagramIdFromWire: creative.instagram_id,
            catalogAccounts: advertiserInstagramCatalog,
          }),
          ...hydratePartnerFieldsFromGuidedWire(
            creative,
            resolvedPartnerPages.length > 0 ? resolvedPartnerPages : pages,
            resolvedPartnerInstagramAccounts
          ),
        };
      }

      const partnerPage = resolveCreativePartnerFacebookPage(
        prev,
        resolvedPartnerPages.length > 0 ? resolvedPartnerPages : pages
      );
      if (partnerPage) {
        const linkedPartnerFields = partnerInstagramFieldsFromLinkedFacebookPage(
          partnerPage,
          resolvedPartnerInstagramAccounts
        );
        if (linkedPartnerFields) {
          const needsPartnerIdSync =
            (prev.partnerInstagramUserId ?? "") !==
            (linkedPartnerFields.partnerInstagramUserId ?? "");
          const needsPartnerAccountSync =
            Boolean(linkedPartnerFields.partnerInstagramAccount) &&
            (prev.partnerInstagramAccount?.id ?? "") !==
              (linkedPartnerFields.partnerInstagramAccount?.id ?? "");
          if (needsPartnerIdSync || needsPartnerAccountSync) {
            return {
              ...prev,
              ...linkedPartnerFields,
            };
          }
        }
      }

      const wantedId = String(prev.instagramAccount?.id ?? "").trim();
      if (!wantedId) {
        return prev;
      }
      const mapped =
        advertiserInstagramCatalog.find((account) => account.id === wantedId) ??
        null;
      if (!mapped) {
        return prev;
      }
      if (
        prev.instagramAccount?.username === mapped.username &&
        prev.instagramAccount?.name === mapped.name &&
        prev.instagramAccount?.profile_picture_url ===
          mapped.profile_picture_url
      ) {
        return prev;
      }
      return { ...prev, instagramAccount: mapped };
    });

    if (replaceFromServer) {
      setEditingCreativeName(null);
      uploadRefs.current = {};
    }
  }, [
    advertiserInstagramCatalog,
    creative,
    isOpen,
    modalSourceKey,
    pages,
    resolvedPartnerInstagramAccounts,
    resolvedPartnerPages,
  ]);

  const partnershipAdvertiserIdentity = useMemo(
    () =>
      resolveAdvertiserIdentityForPartnership(
        value,
        advertiserInstagramCatalog
      ),
    [advertiserInstagramCatalog, value]
  );

  useDebouncedPartnershipPartnerOptionsLoads({
    enabled:
      isOpen &&
      Boolean(onRefetchPartnershipPartnerOptions && adAccountId?.trim()),
    partnerPostActive,
    adAccountId: String(adAccountId ?? "").trim() || undefined,
    advertiserIdentities: partnerPostActive
      ? [partnershipAdvertiserIdentity].filter(
          (identity) =>
            identity.advertiserPageId || identity.advertiserInstagramUserId
        )
      : [],
    onLoad: onRefetchPartnershipPartnerOptions,
  });

  const handleFetchLatestMetaIdentity = useCallback(async () => {
    if (!onFetchLatestMetaIdentity) {
      return;
    }
    setIsManualMetaIdentityRefreshing(true);
    try {
      await onFetchLatestMetaIdentity();
    } finally {
      setIsManualMetaIdentityRefreshing(false);
    }
  }, [onFetchLatestMetaIdentity]);

  const refreshPartnershipOptions = useCallback(async () => {
    const accountId = String(adAccountId ?? "").trim();
    if (!onRefetchPartnershipPartnerOptions || !accountId) {
      return;
    }
    await onRefetchPartnershipPartnerOptions(
      {
        ad_account_id: accountId,
        advertiser_page_id:
          partnershipAdvertiserIdentity.advertiserPageId || undefined,
        advertiser_instagram_user_id:
          partnershipAdvertiserIdentity.advertiserInstagramUserId ||
          undefined,
      },
      { force: true }
    );
  }, [
    adAccountId,
    onRefetchPartnershipPartnerOptions,
    partnershipAdvertiserIdentity.advertiserInstagramUserId,
    partnershipAdvertiserIdentity.advertiserPageId,
  ]);

  const pageOptions = useMemo(
    () => pages.map((page) => ({ label: page.name, value: page })),
    [pages]
  );
  const instagramOptions = useMemo(
    () =>
      instagramSelectOptionsFromAccounts(
        advertiserInstagramCatalog,
        value.instagramAccount
      ),
    [advertiserInstagramCatalog, value.instagramAccount]
  );

  const createUploadUrlForSlot =
    (slotKey: string) =>
    ({
      file,
      callback,
    }: {
      file: File;
      callback: (r: { signedUrl: string }) => void | Promise<string | void>;
    }) =>
      new Promise<{ signedUrl: string; fileKey: string }>((resolve) => {
        createUploadUrl(file, (res: any) => {
          const fileKey = res?.fileKey ?? res?.file_key ?? "";
          const signedUrl = res?.signedUrl ?? res?.signed_url ?? "";
          const cdn_url = res?.cdn_url ?? "";
          uploadRefs.current[slotKey] = { fileKey, cdn_url };
          callback({ signedUrl });
          resolve({ signedUrl, fileKey });
        });
      });

  const selectContainerStyles = {
    container: () => ({
      position: "relative" as const,
      width: "100%",
      backgroundColor: "white",
      cursor: "pointer",
    }),
    dropdownIndicator: (provided: Record<string, unknown>) => ({
      ...provided,
      backgroundColor: "white",
      color: "gray.500",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };

  const toGuidedCreative = (): GuidedCreative =>
    mapCreativeBlockToGuidedCreative(value, { partnerPostsEnabled });

  const hasMixedMediaTypes = useMemo(
    () => hasMixedGuidedCreativeMediaTypes(value),
    [value]
  );
  const hasPrimaryText = value.primaryText.trim().length > 0;
  const hasFacebookPage = Boolean(value.fbPage?.id);
  const hasAtLeastOneMedia =
    (Boolean(value.squareMediaUrl || value.squareMediaFileKey) &&
      Boolean(value.squareMediaType)) ||
    (Boolean(value.verticalMediaUrl || value.verticalMediaFileKey) &&
      Boolean(value.verticalMediaType));
  const destinationLinkError = creativeDestinationLinkError(value.link);
  const isFormValid =
    hasAtLeastOneMedia &&
    hasPrimaryText &&
    hasFacebookPage &&
    !hasMixedMediaTypes &&
    !destinationLinkError;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Ad Creative</ModalHeader>
        <ModalBody>
          <CreativeBlockEditor
            creative={value}
            index={0}
            totalCreatives={1}
            partnerPostsEnabled={partnerPostsEnabled}
            pageOptions={pageOptions}
            instagramOptions={instagramOptions}
            createUploadUrlForSlot={createUploadUrlForSlot}
            uploadRefs={uploadRefs}
            onUpdate={(_, patch) =>
              setValue((prev) => ({
                ...prev,
                ...patch,
              }))
            }
            onRemove={() => {}}
            onAddMore={() => {}}
            editingName={editingCreativeName}
            onEditingNameChange={setEditingCreativeName}
            mediaValidationMessage={
              hasMixedMediaTypes ? MATCHING_CREATIVE_MEDIA_TYPES_ERROR : null
            }
            allowCreativeCountChanges={false}
            instagramAccountClearDisabled={otherAdsInAdsetHaveInstagram}
            isFacebookPagesLoading={isFacebookPagesLoading}
            isInstagramAccountsLoading={isInstagramAccountsLoading}
            onFetchLatestMetaIdentity={handleFetchLatestMetaIdentity}
            isFetchLatestMetaIdentityLoading={isManualMetaIdentityRefreshing}
            adAccountId={adAccountId}
            partnerPages={partnerPostActive ? resolvedPartnerPages : undefined}
            partnerInstagramAccounts={
              partnerPostActive ? resolvedPartnerInstagramAccounts : undefined
            }
            partnershipOptionsLoading={
              partnerPostActive
                ? partnershipOptionsLoading && !partnershipCatalogSlice
                : false
            }
            partnershipPermissionsLoaded={
              partnerPostActive
                ? partnershipCatalogSlice?.partnershipPermissionsLoaded ??
                  partnershipPermissionsLoaded
                : false
            }
            partnershipOptionsError={
              partnerPostActive ? partnershipOptionsError : null
            }
            metaBusinessId={partnerPostActive ? metaBusinessId : undefined}
            advertiserValidationMessages={
              partnerPostActive
                ? partnershipCatalogSlice?.advertiserValidationMessages ??
                  advertiserValidationMessages
                : []
            }
            onRefreshPartnershipOptions={
              partnerPostActive ? refreshPartnershipOptions : undefined
            }
            onPartnershipPermissionLookupLoadingChange={
              partnerPostActive ? setPartnershipPermissionLookupLoading : undefined
            }
            selectContainerStyles={selectContainerStyles}
          />
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              bg="gray.900"
              color="white"
              _hover={{ bg: "gray.800" }}
              onClick={() => {
                if (!isFormValid) return;
                onSave(toGuidedCreative());
              }}
              isLoading={isSaving}
              isDisabled={
                isFacebookPagesLoading ||
                isInstagramAccountsLoading ||
                partnershipPermissionLookupLoading ||
                isSaving ||
                !isFormValid
              }
            >
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
